import React, { useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import dictionaries from "./11111.json";
import { getAnalyzeActAPIUrl, getAPIUrl } from "../api/apiUtils";
import "./AnalysisPage.scss";
import { useRegionsData } from "../hooks/useRegions";
import {
  SUMMATION_FEATURES,
  TARGET_MARKERS,
  getAnomalityMarkerLabel,
  anomalityMarkerCategories,
} from "../Analysis_A/anomalityMarkersDictionary";
import GroupSelector from "../components/GroupSelector";
import TaskSessionsTable, { isAnalysisTask } from "./TaskSessionsTable";
import LlmTaskRuns from "./LlmTaskRuns";
import { apiFetch } from "../api/apiClient";

const ANALYZE_API_BASE_URL = getAPIUrl();
const ANALYZE_ACT_API_BASE_URL = getAnalyzeActAPIUrl();

const analysisTypes = [
  "Центральность",
  "Активность",    
  "Расчет текстовых маркеров",
  "Анализ интенсивности тематик",
  "Анализ аномальности дискуссий",
  "Анализ временных рядов",
  "SRL",
  "Нейроанализ текста",
  "Анализ фрустрации",
  "LLM: эмоции и причины"
];

const multiSelectAnalysisTypes = [
 "Анализ интенсивности тематик",
    "Анализ аномальности дискуссий",
    "Анализ временных рядов",
    "Расчет текстовых маркеров",
    "SRL",
    "Нейроанализ текста",
    "Анализ фрустрации",
    "LLM: эмоции и причины"
];

const AnalysisPage = () => {
  const { regions: groups, isLoading: isGroupsLoading } = useRegionsData();
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const detailsRef = useRef(null);
  useEffect(() => { if (selectedTask) detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [selectedTask]);
  const [selectedAnalysisType, setSelectedAnalysisType] = useState("");
  const [selectedMainDict, setSelectedMainDict] = useState("tgw");
  const [selectedSubDict, setSelectedSubDict] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [llmPeriodic, setLlmPeriodic] = useState(false);
  const [llmInterval, setLlmInterval] = useState(24);
  const [llmUnit, setLlmUnit] = useState('hours');
  const [llmForever, setLlmForever] = useState(false);
  const [llmRepeats, setLlmRepeats] = useState(2);
  const [llmWindow, setLlmWindow] = useState('previous_day');
  const [searchTerm, setSearchTerm] = useState("");
  const [showCustomDicts, setShowCustomDicts] = useState(false);
  const [customDictionaries, setCustomDictionaries] = useState([]);
  const [selectedCustomDict, setSelectedCustomDict] = useState(null);
  const [selectedSummationFeatures, setSelectedSummationFeatures] =
    useState(SUMMATION_FEATURES);
  const [selectedTargetMarkers, setSelectedTargetMarkers] = useState(TARGET_MARKERS);

  const mainDictionaries = Object.keys(dictionaries);
  const subDictionaries = dictionaries[selectedMainDict]
    ? Object.keys(dictionaries[selectedMainDict])
    : [];

  const isMultiSelect = multiSelectAnalysisTypes.includes(selectedAnalysisType);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tasksRes = await apiFetch(`${ANALYZE_API_BASE_URL}/common/tasks`);
        if (!tasksRes.ok) throw new Error("Не удалось загрузить список задач.");
        const tasksData = await tasksRes.json();
        setTasks(tasksData);
      } catch (error) {
        console.error("Ошибка загрузки задач:", error);
      }
    };

    fetchData();
    const timer = setInterval(fetchData, 10000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (subDictionaries.length > 0 && !subDictionaries.includes(selectedSubDict)) {
      setSelectedSubDict(subDictionaries[0]);
    } else if (subDictionaries.length === 0) {
      setSelectedSubDict("");
    }
  }, [selectedMainDict, subDictionaries, selectedSubDict]);

  useEffect(() => {
    if (showCustomDicts) {
      apiFetch(`${getAPIUrl()}/crud/table/dictionaries`)
        .then((res) => res.json())
        .then((data) => setCustomDictionaries(data.data || []));
    }
  }, [showCustomDicts]);

  const handleAnalysisTypeChange = (event) => {
    setSelectedAnalysisType(event.target.value);
    setSelectedGroups([]);
  };

  const handleMainDictChange = (event) => {
    setSelectedMainDict(event.target.value);
  };

  const handleSubDictChange = (event) => {
    setSelectedSubDict(event.target.value);
  };

  const handleSummationFeatureToggle = (feature) => {
    setSelectedSummationFeatures((prev) =>
      prev.includes(feature) ? prev.filter((f) => f !== feature) : [...prev, feature]
    );
  };

  const handleTargetMarkerToggle = (marker) => {
    setSelectedTargetMarkers((prev) =>
      prev.includes(marker) ? prev.filter((m) => m !== marker) : [...prev, marker]
    );
  };

  const handleSummationCategoryToggle = (categoryMarkers, checked) => {
    const relevantMarkers = categoryMarkers.filter((marker) =>
      SUMMATION_FEATURES.includes(marker)
    );
    setSelectedSummationFeatures((prev) => {
      if (checked) {
        return [...new Set([...prev, ...relevantMarkers])];
      } else {
        return prev.filter((marker) => !relevantMarkers.includes(marker));
      }
    });
  };

  const handleTargetCategoryToggle = (categoryMarkers, checked) => {
    const relevantMarkers = categoryMarkers.filter((marker) =>
      TARGET_MARKERS.includes(marker)
    );
    setSelectedTargetMarkers((prev) => {
      if (checked) {
        return [...new Set([...prev, ...relevantMarkers])];
      } else {
        return prev.filter((marker) => !relevantMarkers.includes(marker));
      }
    });
  };

  const getGroupTitle = (groupId) => {
    const normalizedGroupId = String(groupId);
    for (const region of groups) {
      const group = region.groups.find(
        (g) => String(g.group_id) === normalizedGroupId || String(g.id) === normalizedGroupId
      );
      if (group) return group.title;
    }
    return "";
  };

  const getTaskCommunityIds = (task) => {
    const communities = task?.data?.communities;
    if (Array.isArray(communities)) {
      return communities.filter(Boolean).map(String);
    }
    if (typeof communities === "string" && communities.trim()) {
      return communities.split(",").map((item) => item.trim()).filter(Boolean);
    }
    const communityId = task?.data?.community_id || task?.community_id || task?.group_id;
    return communityId ? [String(communityId)] : [];
  };

  const formatTaskCommunityIds = (task) => {
    const ids = getTaskCommunityIds(task);
    return ids.length > 0 ? ids.join(", ") : "-";
  };

  const formatTaskCommunityTitles = (task) => {
    const titles = getTaskCommunityIds(task)
      .map((id) => getGroupTitle(id))
      .filter(Boolean);
    return titles.length > 0 ? titles.join(", ") : "-";
  };

  const fetchTasks = async () => {
    try {
      const response = await apiFetch(`${ANALYZE_API_BASE_URL}/common/tasks`);
      if (!response.ok) throw new Error("Не удалось загрузить задачи.");
      const tasksData = await response.json();
      setTasks(tasksData);
    } catch (error) {
      console.error("Ошибка загрузки задач:", error);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (selectedGroups.length === 0) {
      alert("Выберите хотя бы одну группу.");
      return;
    }

    try {
      let response;
      const formData = new FormData(event.target);
      if (selectedAnalysisType === 'LLM: эмоции и причины') {
        const multiplier = { minutes: 1, hours: 60, days: 1440 }[llmUnit];
        formData.set('interval', llmPeriodic ? Number(llmInterval) * multiplier : 0);
        formData.set('max_repeats', llmPeriodic ? (llmForever ? -1 : llmRepeats) : 1);
        formData.set('window_mode', llmPeriodic ? llmWindow : 'selected');
      }
      const storedUser = JSON.parse(localStorage.getItem("user") || "null");
      if (storedUser?.id) {
        formData.set("user_id", storedUser.id);
      }

      if (
        selectedAnalysisType === "Анализ интенсивности тематик"
      ) {
        const dictValue = selectedSubDict
          ? `${selectedMainDict}, ${selectedSubDict}`
          : selectedMainDict;
        formData.set("dict", dictValue);
        if (showCustomDicts && selectedCustomDict) {
          formData.set("custom_dict_id", selectedCustomDict.id);
          formData.set("custom_dict_title", selectedCustomDict.title);
          formData.set("custom_dict_words", selectedCustomDict.words);
        }
      }

      if (!isMultiSelect) {
        formData.set("community_id", selectedGroups[0] || "");
      } else {
        formData.set("communities", selectedGroups.join(","));
      }

      if (
          selectedAnalysisType === "Анализ аномальности дискуссий"
      ) {
        formData.set("features_to_sum", selectedSummationFeatures.join(","));
        formData.set("target_markers", selectedTargetMarkers.join(","));
      }

      response = await apiFetch(
        `${ANALYZE_ACT_API_BASE_URL}/tasks/submit_analysis_task`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Ошибка отправки: ${await response.text()}`);
      }

      alert("Задача успешно создана.");
      fetchTasks();
    } catch (error) {
      console.error("Ошибка создания задачи:", error);
      alert("Не удалось создать задачу. Попробуйте снова.");
    }
  };


  return (
    <div className="container mt-4 analysis-page">
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <h1 className="card-title mb-4">Анализ данных</h1>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <GroupSelector
                groups={groups}
                isLoading={isGroupsLoading}
                selectedIds={selectedGroups}
                onChange={(ids) => setSelectedGroups(ids)}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                selectionType={isMultiSelect ? "multi" : "single"}
                disabled={!selectedAnalysisType}
                label="Сообщества"
                placeholder="Название или ID"
              />
            </div>

            <div className="row mb-4">
              <div className="col-md-6 mb-3">
                <label htmlFor="start_date" className="form-label fw-bold">
                  Дата начала
                </label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  className="form-control"
                  required={selectedAnalysisType !== 'LLM: эмоции и причины' || !llmPeriodic || llmWindow === 'selected'}
                  disabled={selectedAnalysisType === 'LLM: эмоции и причины' && llmPeriodic && llmWindow === 'previous_day'}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="end_date" className="form-label fw-bold">
                  Дата окончания
                </label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  className="form-control"
                  required={selectedAnalysisType !== 'LLM: эмоции и причины' || !llmPeriodic || llmWindow === 'selected'}
                  disabled={selectedAnalysisType === 'LLM: эмоции и причины' && llmPeriodic && llmWindow === 'previous_day'}
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="analysis_type" className="form-label fw-bold">
                Тип анализа
              </label>
              <select
                id="analysis_type"
                name="analysis_type"
                className="form-select"
                required
                onChange={handleAnalysisTypeChange}
                value={selectedAnalysisType}
              >
                <option value="">Выберите тип анализа</option>
                {analysisTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {selectedAnalysisType === 'LLM: эмоции и причины' && (
              <div className="mb-4 p-3 border rounded">
                <p>Анализ сохранённых постов и комментариев. Парсинг запускается отдельно. Результаты — «Эмотивный анализ → Причины эмоций LLM». Дневные статусы и повтор ошибок — в таблице ниже.</p>
                <label className="form-check-label"><input className="form-check-input me-2" type="checkbox" checked={llmPeriodic} onChange={e => setLlmPeriodic(e.target.checked)} />Периодическая задача</label>
                {llmPeriodic && <div className="mt-3">
                  <label className="form-label" htmlFor="llm-interval">Интервал между запусками</label>
                  <div className="d-flex gap-2 mb-3">
                    <input id="llm-interval" className="form-control" type="number" min="1" required value={llmInterval} onChange={e => setLlmInterval(e.target.value)} />
                    <select className="form-select" aria-label="Единица интервала" value={llmUnit} onChange={e => setLlmUnit(e.target.value)}><option value="minutes">Минуты</option><option value="hours">Часы</option><option value="days">Дни</option></select>
                  </div>
                  <label className="form-label" htmlFor="llm-window">Период данных при каждом запуске</label>
                  <select id="llm-window" className="form-select mb-3" value={llmWindow} onChange={e => setLlmWindow(e.target.value)}><option value="previous_day">Предыдущий завершённый день (Москва)</option><option value="selected">Указанный диапазон дат</option></select>
                  <label className="form-check-label"><input className="form-check-input me-2" type="checkbox" checked={llmForever} onChange={e => setLlmForever(e.target.checked)} />Повторять без ограничения</label>
                  {!llmForever && <label className="form-label d-block mt-2">Число запусков, включая первый<input className="form-control" type="number" min="1" required value={llmRepeats} onChange={e => setLlmRepeats(e.target.value)} /></label>}
                  <p className="text-muted mt-2">Первый запуск сразу после создания. Следующий начнётся, когда завершится предыдущий. Интервал считается в минутах/часах, привязку парсера к полуночи настройте в задачах сбора.</p>
                </div>}
              </div>
            )}

            {selectedAnalysisType === "Активность" && (
              <div className="row mb-4">
                <div className="col-md-6 mb-3">
                  <label htmlFor="mode" className="form-label fw-bold">
                    Mode
                  </label>
                  <input
                    type="number"
                    id="mode"
                    name="mode"
                    className="form-control"
                    defaultValue="2"
                    min="1"
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="step" className="form-label fw-bold">
                    Шаг (c)
                  </label>
                  <input
                    type="number"
                    id="step"
                    name="step"
                    className="form-control"
                    defaultValue="7200"
                    min="1"
                    required
                  />
                </div>
              </div>
            )}

            {selectedAnalysisType === "Анализ интенсивности тематик" && (
              <div className="row mb-4">
                {!showCustomDicts && (
                  <>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="main_dict" className="form-label fw-bold">
                        Главный словарь
                      </label>
                      <select
                        id="main_dict"
                        name="main_dict"
                        className="form-select"
                        required
                        onChange={handleMainDictChange}
                        value={selectedMainDict}
                      >
                        {mainDictionaries.map((dict) => (
                          <option key={dict} value={dict}>
                            {dict}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="sub_dict" className="form-label fw-bold">
                        Подсловарь
                      </label>
                      <select
                        id="sub_dict"
                        name="sub_dict"
                        className="form-select"
                        required
                        onChange={handleSubDictChange}
                        value={selectedSubDict}
                        disabled={subDictionaries.length === 0}
                      >
                        {subDictionaries.length > 0 ? (
                          subDictionaries.map((dict) => (
                            <option key={dict} value={dict}>
                              {dict}
                            </option>
                          ))
                        ) : (
                          <option value="">Подсловарей нет</option>
                        )}
                      </select>
                    </div>
                  </>
                )}
                <div className="col-md-12 mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="customDictsCheckbox"
                      checked={showCustomDicts}
                      onChange={(e) => setShowCustomDicts(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="customDictsCheckbox">
                      Использовать пользовательский словарь
                    </label>
                  </div>
                </div>
                {showCustomDicts && (
                  <div className="col-md-12 mb-3">
                    <label className="form-label fw-bold">
                      Выберите пользовательский словарь
                    </label>
                    <select
                      className="form-select"
                      value={selectedCustomDict ? selectedCustomDict.id : ""}
                      onChange={(e) => {
                        const dict = customDictionaries.find(
                          (d) => d.id === Number(e.target.value)
                        );
                        setSelectedCustomDict(dict);
                      }}
                    >
                      <option value="">Выберите словарь</option>
                      {customDictionaries.map((dict) => (
                        <option key={dict.id} value={dict.id}>
                          {dict.title}
                        </option>
                      ))}
                    </select>
                    {selectedCustomDict && (
                      <div className="mt-2 p-2 border rounded bg-light">
                        <b>Содержимое словаря:</b>
                        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {(
                            Array.isArray(selectedCustomDict.words)
                              ? selectedCustomDict.words
                              : (selectedCustomDict.words || "")
                                  .replace(/[{}()]/g, "")
                                  .split(/[\n,]+/)
                                  .map((w) => w.trim())
                                  .filter((w) => w)
                          ).join(", ")}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="col-md-12">
                  <small className="form-text text-muted">
                    {!showCustomDicts && (
                      <>
                        Текущий словарь: {selectedMainDict}
                        {selectedSubDict && `, ${selectedSubDict}`}
                      </>
                    )}
                  </small>
                </div>
              </div>
            )}

            {selectedAnalysisType ===
              "Анализ аномальности дискуссий" && (
              <div className="mb-4">
                <div className="mb-4">
                  <label className="form-label fw-bold mb-3">
                    Показатели для суммирования ({selectedSummationFeatures.length})
                  </label>
                  <div className="row">
                    {SUMMATION_FEATURES.map((feature) => (
                      <div key={feature} className="col-md-6 col-lg-4 mb-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`summation-${feature}`}
                            checked={selectedSummationFeatures.includes(feature)}
                            onChange={() => handleSummationFeatureToggle(feature)}
                          />
                          <label className="form-check-label" htmlFor={`summation-${feature}`}>
                            {getAnomalityMarkerLabel(feature)}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold mb-3">
                    Целевые маркеры ({selectedTargetMarkers.length})
                  </label>

                  <div className="mb-3">
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setSelectedTargetMarkers(TARGET_MARKERS)}
                      >
                        Выбрать все
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setSelectedTargetMarkers([])}
                      >
                        Очистить
                      </button>
                    </div>
                  </div>

                  {anomalityMarkerCategories.map((category) => {
                    const categoryTargetMarkers = category.markers.filter((marker) =>
                      TARGET_MARKERS.includes(marker)
                    );
                    if (categoryTargetMarkers.length === 0) return null;

                    const allSelected = categoryTargetMarkers.every((marker) =>
                      selectedTargetMarkers.includes(marker)
                    );
                    const someSelected = categoryTargetMarkers.some((marker) =>
                      selectedTargetMarkers.includes(marker)
                    );

                    return (
                      <div key={category.category} className="mb-3 p-3 border rounded bg-light">
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`category-${category.category}`}
                            checked={allSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = someSelected && !allSelected;
                            }}
                            onChange={(e) =>
                              handleTargetCategoryToggle(categoryTargetMarkers, e.target.checked)
                            }
                          />
                          <label
                            className="form-check-label fw-bold"
                            htmlFor={`category-${category.category}`}
                          >
                            {category.category} (
                            {
                              categoryTargetMarkers.filter((m) =>
                                selectedTargetMarkers.includes(m)
                              ).length
                            }
                            /{categoryTargetMarkers.length})
                          </label>
                        </div>
                        <div className="row ms-3">
                          {categoryTargetMarkers.map((marker) => (
                            <div key={marker} className="col-md-6 col-lg-4 mb-1">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`target-${marker}`}
                                  checked={selectedTargetMarkers.includes(marker)}
                                  onChange={() => handleTargetMarkerToggle(marker)}
                                />
                                <label className="form-check-label" htmlFor={`target-${marker}`}>
                                  {getAnomalityMarkerLabel(marker)}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedAnalysisType === "Анализ временных рядов" && (
              <div className="row mb-4">
                <div className="col-md-6 mb-3">
                  <label htmlFor="marker_to_run" className="form-label fw-bold">
                    Маркер для анализа
                  </label>
                  <input
                    type="text"
                    id="marker_to_run"
                    name="marker_to_run"
                    className="form-control"
                    defaultValue="trager_coef"
                    placeholder="trager_coef"
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="max_repeats" className="form-label fw-bold">
                    Максимальное число повторов
                  </label>
                  <input
                    type="number"
                    id="max_repeats"
                    name="max_repeats"
                    className="form-control"
                    defaultValue="1"
                    min="1"
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="interval" className="form-label fw-bold">
                    Интервал (c)
                  </label>
                  <input
                    type="number"
                    id="interval"
                    name="interval"
                    className="form-control"
                    defaultValue="0"
                    min="0"
                    required
                  />
                </div>
              </div>
            )}

            {selectedAnalysisType === "SRL" && (
              <div className="row mb-4">
                <div className="col-md-6 mb-3">
                  <label htmlFor="confidence_threshold" className="form-label fw-bold">
                    Минимальный порог уверенности
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="confidence_threshold"
                    name="confidence_threshold"
                    className="form-control"
                    defaultValue="0.5"
                    min="0"
                    max="1"
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="disable_predicate_detection"
                      name="disable_predicate_detection"
                      className="form-check-input"
                      defaultChecked={true}
                    />
                    <label
                      htmlFor="disable_predicate_detection"
                      className="form-check-label fw-bold"
                    >
                      Отключить выделение предикатов
                    </label>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="do_filtration"
                      name="do_filtration"
                      className="form-check-input"
                      defaultChecked={true}
                    />
                    <label htmlFor="do_filtration" className="form-check-label fw-bold">
                      Включить фильтрацию
                    </label>
                  </div>
                </div>
              </div>
            )}

            <button type="submit" className="btn btn-primary btn-lg">
              Создать задачу
            </button>
          </form>
        </div>
      </div>

      <TaskSessionsTable tasks={tasks.filter(isAnalysisTask)} title="Задачи анализа" getTitle={formatTaskCommunityTitles} getIds={formatTaskCommunityIds} onSelect={setSelectedTask} selectedId={selectedTask?.task_id} deletingId={deletingTaskId} onDelete={async task => {
        if (!window.confirm(`Удалить периодическую задачу ${task.task_id}?`)) return;
        setDeletingTaskId(task.task_id);
        try { const response = await apiFetch(`${ANALYZE_ACT_API_BASE_URL}/tasks/task/${encodeURIComponent(task.task_id)}`, { method: 'DELETE' }); if (!response.ok) throw new Error(await response.text()); await fetchTasks(); if (selectedTask?.task_id === task.task_id) setSelectedTask(null); }
        catch (err) { alert(err.message); } finally { setDeletingTaskId(null); }
      }} />
      <div ref={detailsRef}>{selectedTask && <LlmTaskRuns key={selectedTask.task_id} task={selectedTask} onClose={() => setSelectedTask(null)} />}</div>
    </div>
  );
};

export default AnalysisPage;
